import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions,
  ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList,
  ActivityIndicator, TextInput, Switch,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const SECONDARY = '#1e7be2';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async (config) => {
  try { const t = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token'); if (t) config.headers.Authorization = `Bearer ${t}`; } catch {} return config;
});

const EP = {
  companyProfile: (id) => `/api/company-profile/${id}`,
  deals: '/api/data/crm_deals', contacts: '/api/data/crm_contacts', activities: '/api/data/crm_activities',
  stages: '/api/data/crm_stages', leads: '/api/data/leads', leadSources: '/api/data/lead_sources',
  leadScores: '/api/data/lead_scores', employees: '/api/employees', clients: '/api/clients', campaigns: '/api/campaigns',
};
const getBusinessId = async () => { try { return await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id') || '1'; } catch { return '0'; } };
const safeData = (res) => (res?.data?.success && Array.isArray(res.data.data)) ? res.data.data : [];

export default function LeadManagementScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);

  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stages, setStages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leadStats, setLeadStats] = useState({ total: 0, new: 0, contacted: 0, qualified: 0, won: 0, lost: 0, totalValue: 0, avgValue: 0, conversionRate: 0 });

  const [newLead, setNewLead] = useState({ firstName: '', lastName: '', email: '', phone: '', company: '', jobTitle: '', source: 'website', priority: 'medium', status: 'new', notes: '', value: '' });

  const leadStatuses = ['all', 'new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];
  const leadPriorities = ['all', 'low', 'medium', 'high', 'critical'];
  const leadSourcesList = ['all', 'website', 'referral', 'linkedin', 'trade-show', 'cold-call', 'email-campaign', 'social-media', 'direct'];

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      setIsLoading(true);
      const bizId = route?.params?.company_id || await getBusinessId();
      setCompanyId(bizId);
      await fetchAll(bizId);
    } catch (e) { console.error('Init error:', e); } finally { setIsLoading(false); }
  };

  const onRefresh = async () => { setRefreshing(true); await init(); setRefreshing(false); };

  const fetchAll = async (bizId) => {
    const cid = parseInt(bizId) || 0;
    const p = { company_id: cid };
    const [profileRes, dealsRes, contactsRes, activitiesRes, stagesRes, employeesRes, leadsRes] = await Promise.allSettled([
      api.get(EP.companyProfile(cid)), api.get(EP.deals, { params: p }), api.get(EP.contacts, { params: p }),
      api.get(EP.activities, { params: p }), api.get(EP.stages), api.get(EP.employees, { params: p }),
      api.get(EP.leads, { params: p }),
    ]);

    const profile = profileRes.status === 'fulfilled' ? (profileRes.value?.data?.success ? (Array.isArray(profileRes.value.data.data) ? profileRes.value.data.data[0] : profileRes.value.data.data) : null) : null;
    if (profile) { setCompanyProfile(profile); await AsyncStorage.setItem('company_profile', JSON.stringify(profile)); }
    else { try { const c = await AsyncStorage.getItem('company_profile'); if (c) setCompanyProfile(JSON.parse(c)); } catch {} }

    const deals = safeData(dealsRes.status === 'fulfilled' ? dealsRes.value : null);
    const cts = safeData(contactsRes.status === 'fulfilled' ? contactsRes.value : null);
    const acts = safeData(activitiesRes.status === 'fulfilled' ? activitiesRes.value : null);
    const stgs = safeData(stagesRes.status === 'fulfilled' ? stagesRes.value : null);
    const emps = safeData(employeesRes.status === 'fulfilled' ? employeesRes.value : null);
    const rawLeads = safeData(leadsRes.status === 'fulfilled' ? leadsRes.value : null);

    // Merge crm_deals as leads (deals have stage, value, contacts)
    const allLeads = deals.map((d, idx) => ({
      id: String(d.id), firstName: (d.contact_name || '').split(' ')[0] || '', lastName: (d.contact_name || '').split(' ').slice(1).join(' ') || '',
      email: d.contact_email || '', phone: d.contact_phone || '', company: d.company || '',
      jobTitle: '', source: d.source || 'direct', priority: d.priority || 'medium',
      status: d.stage || 'lead', value: parseFloat(d.value || 0), notes: d.notes || '',
      probability: d.probability || 0, assignedTo: d.assigned_to, tags: d.tags || [],
      createdAt: d.created_at, updatedAt: d.updated_at, dealId: d.id, clientId: d.client_id,
    }));
    // Also add any from leads table
    rawLeads.forEach((l, idx) => {
      if (!allLeads.find(al => al.email === l.email)) {
        allLeads.push({ id: `lead-${l.id}`, firstName: l.first_name || l.name || '', lastName: l.last_name || '',
          email: l.email || '', phone: l.phone || '', company: l.company || '',
          jobTitle: l.job_title || '', source: l.source || 'website', priority: l.priority || 'medium',
          status: l.status || 'new', value: parseFloat(l.value || l.deal_value || 0), notes: l.notes || '',
          probability: l.score || 0, assignedTo: l.assigned_to, tags: [], createdAt: l.created_at, updatedAt: l.updated_at,
        });
      }
    });

    setLeads(allLeads);
    setContacts(cts);
    setActivities(acts);
    setStages(stgs.length > 0 ? stgs : [
      { stage_id: 'lead', name: 'Lead', color: '#94a3b8' }, { stage_id: 'contacted', name: 'Contacted', color: '#3b82f6' },
      { stage_id: 'qualified', name: 'Qualified', color: '#f59e0b' }, { stage_id: 'proposal', name: 'Proposal', color: '#8b5cf6' },
      { stage_id: 'negotiation', name: 'Negotiation', color: '#ec4899' }, { stage_id: 'closed_won', name: 'Won', color: '#10b981' },
      { stage_id: 'closed_lost', name: 'Lost', color: '#ef4444' },
    ]);
    setEmployees(emps);

    // Compute stats
    const wonLeads = allLeads.filter(l => l.status === 'won' || l.status === 'closed_won');
    const lostLeads = allLeads.filter(l => l.status === 'lost' || l.status === 'closed_lost');
    const totalValue = allLeads.reduce((s, l) => s + (l.value || 0), 0);
    setLeadStats({
      total: allLeads.length, new: allLeads.filter(l => l.status === 'new' || l.status === 'lead').length,
      contacted: allLeads.filter(l => l.status === 'contacted').length, qualified: allLeads.filter(l => l.status === 'qualified').length,
      won: wonLeads.length, lost: lostLeads.length, totalValue,
      avgValue: allLeads.length > 0 ? totalValue / allLeads.length : 0,
      conversionRate: allLeads.length > 0 ? (wonLeads.length / allLeads.length) * 100 : 0,
    });
  };

  const createLead = async () => {
    if (!newLead.firstName || !newLead.email || !newLead.company) {
      Alert.alert('Required', 'First name, email and company are required'); return;
    }
    try {
      const bizId = parseInt(companyId) || 0;
      await api.post(EP.deals, {
        company: newLead.company, contact_name: `${newLead.firstName} ${newLead.lastName}`.trim(),
        contact_email: newLead.email, contact_phone: newLead.phone, stage: 'lead',
        value: parseFloat(newLead.value) || 0, probability: 10, priority: newLead.priority,
        source: newLead.source, notes: newLead.notes, company_id: bizId, tags: [],
      });
      Alert.alert('Success', 'Lead created successfully');
      setShowAddLeadModal(false);
      setNewLead({ firstName: '', lastName: '', email: '', phone: '', company: '', jobTitle: '', source: 'website', priority: 'medium', status: 'new', notes: '', value: '' });
      await fetchAll(companyId);
    } catch (e) { Alert.alert('Error', e.message); }
  };

  const updateLeadStatus = async (lead, newStatus) => {
    try {
      if (lead.dealId) {
        await api.put(`${EP.deals}/${lead.dealId}`, { stage: newStatus });
      }
      Alert.alert('Updated', `Lead moved to ${newStatus}`);
      await fetchAll(companyId);
    } catch (e) { Alert.alert('Error', e.message); }
  };

  const deleteLead = (lead) => {
    Alert.alert('Delete Lead', `Delete ${lead.firstName} ${lead.lastName}?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try {
          if (lead.dealId) await api.delete(`${EP.deals}/${lead.dealId}`);
          Alert.alert('Deleted');
          await fetchAll(companyId);
        } catch (e) { Alert.alert('Error', e.message); }
      }},
    ]);
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: companyProfile?.currency || 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const getStatusColor = (s) => ({ new: '#6B7280', lead: '#6B7280', contacted: '#3B82F6', qualified: '#F59E0B', proposal: '#8B5CF6', negotiation: '#EC4899', won: '#10B981', closed_won: '#10B981', lost: '#EF4444', closed_lost: '#EF4444' }[s] || '#6B7280');
  const getPriorityColor = (p) => ({ critical: '#EF4444', high: '#F59E0B', medium: '#3B82F6', low: '#10B981' }[p] || '#6B7280');

  const filteredLeads = leads.filter(l => {
    if (filterStatus !== 'all' && l.status !== filterStatus) return false;
    if (filterPriority !== 'all' && l.priority !== filterPriority) return false;
    if (filterSource !== 'all' && l.source !== filterSource) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return `${l.firstName} ${l.lastName} ${l.email} ${l.company}`.toLowerCase().includes(q);
    }
    return true;
  });

  // ── RENDERS ──
  const renderHeader = () => (
    <LinearGradient colors={[PRIMARY, SECONDARY]} style={s.header}>
      <View style={s.headerRow}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={s.headerBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" /></TouchableOpacity>
        <View style={s.headerCenter}>
          <Text style={s.headerTitle}>{companyProfile?.name || 'Lead Management'}</Text>
          <Text style={s.headerSub}>{companyProfile?.industry ? `${companyProfile.industry} · ` : ''}{leads.length} leads</Text>
        </View>
        <TouchableOpacity onPress={() => setShowAddLeadModal(true)} style={s.headerBtn}><MaterialCommunityIcons name="plus" size={24} color="#FFF" /></TouchableOpacity>
      </View>
      {companyProfile && (
        <View style={s.chips}>
          {companyProfile.headquarters && <View style={s.chip}><MaterialCommunityIcons name="map-marker" size={12} color="#93C5FD" /><Text style={s.chipText}>{companyProfile.headquarters}</Text></View>}
          {companyProfile.employee_count && <View style={s.chip}><MaterialCommunityIcons name="account-group" size={12} color="#93C5FD" /><Text style={s.chipText}>{companyProfile.employee_count} team</Text></View>}
        </View>
      )}
    </LinearGradient>
  );

  const renderStats = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.statsRow} contentContainerStyle={{ paddingHorizontal: 16 }}>
      {[
        { label: 'Total', value: leadStats.total, icon: 'account-multiple', color: PRIMARY },
        { label: 'New', value: leadStats.new, icon: 'account-plus', color: '#6B7280' },
        { label: 'Qualified', value: leadStats.qualified, icon: 'account-check', color: '#F59E0B' },
        { label: 'Won', value: leadStats.won, icon: 'trophy', color: '#10B981' },
        { label: 'Lost', value: leadStats.lost, icon: 'account-remove', color: '#EF4444' },
        { label: 'Pipeline', value: formatCurrency(leadStats.totalValue), icon: 'cash', color: '#8B5CF6' },
        { label: 'Conversion', value: `${leadStats.conversionRate.toFixed(1)}%`, icon: 'percent', color: '#3B82F6' },
      ].map((st, idx) => (
        <View key={idx} style={s.statCard}>
          <MaterialCommunityIcons name={st.icon} size={20} color={st.color} />
          <Text style={[s.statValue, { color: st.color }]}>{typeof st.value === 'number' ? st.value : st.value}</Text>
          <Text style={s.statLabel}>{st.label}</Text>
        </View>
      ))}
    </ScrollView>
  );

  const renderFilters = () => (
    <View style={s.filtersContainer}>
      <View style={s.searchRow}>
        <MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF" />
        <TextInput style={s.searchInput} placeholder="Search leads..." placeholderTextColor="#9CA3AF" value={searchQuery} onChangeText={setSearchQuery} />
        {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><MaterialCommunityIcons name="close" size={18} color="#9CA3AF" /></TouchableOpacity> : null}
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8 }}>
        {leadStatuses.map(st => (
          <TouchableOpacity key={st} style={[s.filterChip, filterStatus === st && { backgroundColor: PRIMARY }]} onPress={() => setFilterStatus(st)}>
            <Text style={[s.filterChipText, filterStatus === st && { color: '#FFF' }]}>{st === 'all' ? 'All Status' : st.charAt(0).toUpperCase() + st.slice(1)}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 6 }}>
        {leadPriorities.map(p => (
          <TouchableOpacity key={p} style={[s.filterChip, filterPriority === p && { backgroundColor: getPriorityColor(p) }]} onPress={() => setFilterPriority(p)}>
            <Text style={[s.filterChipText, filterPriority === p && { color: '#FFF' }]}>{p === 'all' ? 'All Priority' : p.charAt(0).toUpperCase() + p.slice(1)}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderTabs = () => (
    <View style={s.tabsRow}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 12 }}>
        {[
          { key: 'leads', title: 'Leads', icon: 'account-multiple' },
          { key: 'pipeline', title: 'Pipeline', icon: 'pipe' },
          { key: 'contacts', title: 'Contacts', icon: 'contacts' },
          { key: 'activities', title: 'Activities', icon: 'history' },
          { key: 'analytics', title: 'Analytics', icon: 'chart-bar' },
        ].map(tab => (
          <TouchableOpacity key={tab.key} style={[s.tab, activeTab === tab.key && s.activeTab]} onPress={() => setActiveTab(tab.key)}>
            <MaterialCommunityIcons name={tab.icon} size={18} color={activeTab === tab.key ? PRIMARY : '#6B7280'} />
            <Text style={[s.tabText, activeTab === tab.key && s.activeTabText]}>{tab.title}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderLeadCard = (lead) => (
    <TouchableOpacity key={lead.id} style={s.leadCard} onPress={() => { setSelectedLead(lead); setShowLeadModal(true); }}>
      <View style={s.leadHeader}>
        <View style={s.leadInfo}>
          <View style={[s.avatar, { backgroundColor: getStatusColor(lead.status) }]}>
            <Text style={s.avatarText}>{(lead.firstName[0] || '') + (lead.lastName[0] || '')}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={s.leadName}>{lead.firstName} {lead.lastName}</Text>
            <Text style={s.leadCompany}>{lead.company}{lead.jobTitle ? ` · ${lead.jobTitle}` : ''}</Text>
          </View>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          {lead.value > 0 && <Text style={s.leadValue}>{formatCurrency(lead.value)}</Text>}
          <View style={[s.badge, { backgroundColor: getStatusColor(lead.status) + '20' }]}>
            <Text style={[s.badgeText, { color: getStatusColor(lead.status) }]}>{lead.status}</Text>
          </View>
        </View>
      </View>
      <View style={s.leadFooter}>
        <Text style={s.leadMeta}>{lead.source} · {lead.priority}</Text>
        <Text style={s.leadDate}>{formatDate(lead.createdAt)}</Text>
      </View>
      {lead.probability > 0 && (
        <View style={s.probBar}><View style={[s.probFill, { width: `${lead.probability}%`, backgroundColor: lead.probability > 50 ? '#10B981' : '#F59E0B' }]} /></View>
      )}
    </TouchableOpacity>
  );

  const renderLeadsList = () => (
    <View>
      {renderFilters()}
      {filteredLeads.length === 0 ? (
        <View style={s.empty}><MaterialCommunityIcons name="account-search" size={48} color="#D1D5DB" /><Text style={s.emptyText}>No leads found</Text>
          <TouchableOpacity style={s.emptyBtn} onPress={() => setShowAddLeadModal(true)}><Text style={s.emptyBtnText}>+ Add Lead</Text></TouchableOpacity></View>
      ) : filteredLeads.map(renderLeadCard)}
    </View>
  );

  const renderPipeline = () => (
    <View style={s.section}>
      <Text style={s.sectionTitle}>Pipeline by Stage</Text>
      {stages.map((stage, idx) => {
        const stageLeads = leads.filter(l => l.status === stage.stage_id || l.status === stage.name?.toLowerCase());
        const stageValue = stageLeads.reduce((s2, l) => s2 + (l.value || 0), 0);
        return (
          <View key={idx} style={s.pipelineStage}>
            <View style={s.pipelineHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <View style={[s.dot, { backgroundColor: stage.color || '#6B7280' }]} />
                <Text style={s.pipelineName}>{stage.name}</Text>
                <View style={[s.badge, { backgroundColor: (stage.color || '#6B7280') + '20', marginLeft: 8 }]}>
                  <Text style={[s.badgeText, { color: stage.color || '#6B7280' }]}>{stageLeads.length}</Text>
                </View>
              </View>
              <Text style={s.pipelineValue}>{formatCurrency(stageValue)}</Text>
            </View>
            {stageLeads.slice(0, 3).map(l => (
              <TouchableOpacity key={l.id} style={s.miniCard} onPress={() => { setSelectedLead(l); setShowLeadModal(true); }}>
                <Text style={s.miniName}>{l.firstName} {l.lastName}</Text>
                <Text style={s.miniCompany}>{l.company}</Text>
                {l.value > 0 && <Text style={s.miniValue}>{formatCurrency(l.value)}</Text>}
              </TouchableOpacity>
            ))}
            {stageLeads.length > 3 && <Text style={s.moreText}>+{stageLeads.length - 3} more</Text>}
          </View>
        );
      })}
    </View>
  );

  const renderContacts = () => (
    <View style={s.section}>
      <Text style={s.sectionTitle}>CRM Contacts ({contacts.length})</Text>
      {contacts.length === 0 ? <View style={s.empty}><MaterialCommunityIcons name="contacts-outline" size={48} color="#D1D5DB" /><Text style={s.emptyText}>No contacts</Text></View> :
        contacts.map((c, idx) => (
          <View key={idx} style={s.contactCard}>
            <View style={[s.avatar, { backgroundColor: '#3B82F6', width: 36, height: 36, borderRadius: 18 }]}>
              <Text style={[s.avatarText, { fontSize: 13 }]}>{(c.name || '?')[0]}</Text>
            </View>
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={s.contactName}>{c.name}</Text>
              <Text style={s.contactMeta}>{c.role || ''}{c.company ? ` · ${c.company}` : ''}</Text>
              <Text style={s.contactMeta}>{c.email}{c.phone ? ` · ${c.phone}` : ''}</Text>
            </View>
            {c.score && <View style={[s.scoreBadge, { backgroundColor: c.score >= 70 ? '#10B981' : c.score >= 40 ? '#F59E0B' : '#EF4444' }]}><Text style={s.scoreText}>{c.score}</Text></View>}
          </View>
        ))
      }
    </View>
  );

  const renderActivities = () => (
    <View style={s.section}>
      <Text style={s.sectionTitle}>Recent Activities ({activities.length})</Text>
      {activities.length === 0 ? <View style={s.empty}><MaterialCommunityIcons name="history" size={48} color="#D1D5DB" /><Text style={s.emptyText}>No activities</Text></View> :
        activities.slice(0, 20).map((a, idx) => {
          const icons = { call: 'phone', email: 'email', meeting: 'account-group', note: 'note', task: 'checkbox-marked' };
          const colors = { call: '#3B82F6', email: '#F59E0B', meeting: '#8B5CF6', note: '#6B7280', task: '#10B981' };
          return (
            <View key={idx} style={s.activityCard}>
              <View style={[s.actIcon, { backgroundColor: (colors[a.type] || '#6B7280') + '20' }]}>
                <MaterialCommunityIcons name={icons[a.type] || 'circle'} size={18} color={colors[a.type] || '#6B7280'} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.actTitle}>{a.title}</Text>
                <Text style={s.actDesc}>{a.description || ''}</Text>
                <Text style={s.actMeta}>{a.type} · {a.duration ? `${a.duration}min` : ''} · {formatDate(a.scheduled_at || a.created_at)}</Text>
              </View>
              {a.completed && <MaterialCommunityIcons name="check-circle" size={18} color="#10B981" />}
            </View>
          );
        })
      }
    </View>
  );

  const renderAnalytics = () => {
    const sourceBreakdown = {};
    leads.forEach(l => { sourceBreakdown[l.source || 'unknown'] = (sourceBreakdown[l.source || 'unknown'] || 0) + 1; });
    const statusBreakdown = {};
    leads.forEach(l => { statusBreakdown[l.status || 'unknown'] = (statusBreakdown[l.status || 'unknown'] || 0) + 1; });

    return (
      <View style={s.section}>
        <Text style={s.sectionTitle}>Lead Analytics</Text>
        <View style={s.analyticsGrid}>
          {[
            { label: 'Total Value', value: formatCurrency(leadStats.totalValue), icon: 'cash-multiple', color: '#10B981' },
            { label: 'Avg Deal', value: formatCurrency(leadStats.avgValue), icon: 'calculator', color: '#3B82F6' },
            { label: 'Conversion', value: `${leadStats.conversionRate.toFixed(1)}%`, icon: 'percent', color: '#8B5CF6' },
            { label: 'Contacts', value: String(contacts.length), icon: 'contacts', color: '#F59E0B' },
          ].map((m, idx) => (
            <View key={idx} style={s.analyticsCard}>
              <MaterialCommunityIcons name={m.icon} size={24} color={m.color} />
              <Text style={[s.analyticsValue, { color: m.color }]}>{m.value}</Text>
              <Text style={s.analyticsLabel}>{m.label}</Text>
            </View>
          ))}
        </View>

        <Text style={[s.sectionTitle, { marginTop: 20 }]}>By Source</Text>
        {Object.entries(sourceBreakdown).sort((a, b) => b[1] - a[1]).map(([source, count], idx) => (
          <View key={idx} style={s.breakdownRow}>
            <Text style={s.breakdownLabel}>{source}</Text>
            <View style={s.breakdownBar}><View style={[s.breakdownFill, { width: `${(count / leads.length) * 100}%` }]} /></View>
            <Text style={s.breakdownCount}>{count}</Text>
          </View>
        ))}

        <Text style={[s.sectionTitle, { marginTop: 20 }]}>By Status</Text>
        {Object.entries(statusBreakdown).sort((a, b) => b[1] - a[1]).map(([status, count], idx) => (
          <View key={idx} style={s.breakdownRow}>
            <View style={{ flexDirection: 'row', alignItems: 'center', width: 100 }}>
              <View style={[s.dot, { backgroundColor: getStatusColor(status), marginRight: 6 }]} />
              <Text style={s.breakdownLabel}>{status}</Text>
            </View>
            <View style={s.breakdownBar}><View style={[s.breakdownFill, { width: `${(count / leads.length) * 100}%`, backgroundColor: getStatusColor(status) }]} /></View>
            <Text style={s.breakdownCount}>{count}</Text>
          </View>
        ))}
      </View>
    );
  };

  const renderContent = () => {
    const wrap = (ch) => <ScrollView style={{ flex: 1 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />} showsVerticalScrollIndicator={false}>{ch}<View style={{ height: 40 }} /></ScrollView>;
    switch (activeTab) {
      case 'leads':      return wrap(<>{renderLeadsList()}</>);
      case 'pipeline':   return wrap(<>{renderPipeline()}</>);
      case 'contacts':   return wrap(<>{renderContacts()}</>);
      case 'activities': return wrap(<>{renderActivities()}</>);
      case 'analytics':  return wrap(<>{renderAnalytics()}</>);
      default: return null;
    }
  };

  const renderLeadDetailModal = () => (
    <Modal visible={showLeadModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowLeadModal(false)}>
      <View style={s.modalContainer}>
        <View style={s.modalHeader}>
          <Text style={s.modalTitle}>{selectedLead ? `${selectedLead.firstName} ${selectedLead.lastName}` : 'Lead'}</Text>
          <TouchableOpacity style={s.modalClose} onPress={() => setShowLeadModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280" /></TouchableOpacity>
        </View>
        {selectedLead && (
          <ScrollView style={s.modalBody}>
            <View style={s.modalSection}>
              <Text style={s.modalSectionTitle}>Contact Info</Text>
              <Text style={s.modalText}>Company: {selectedLead.company}</Text>
              <Text style={s.modalText}>Email: {selectedLead.email}</Text>
              {selectedLead.phone && <Text style={s.modalText}>Phone: {selectedLead.phone}</Text>}
              {selectedLead.jobTitle && <Text style={s.modalText}>Title: {selectedLead.jobTitle}</Text>}
            </View>
            <View style={s.modalSection}>
              <Text style={s.modalSectionTitle}>Lead Details</Text>
              <Text style={s.modalText}>Status: {selectedLead.status}</Text>
              <Text style={s.modalText}>Priority: {selectedLead.priority}</Text>
              <Text style={s.modalText}>Source: {selectedLead.source}</Text>
              <Text style={s.modalText}>Value: {formatCurrency(selectedLead.value)}</Text>
              <Text style={s.modalText}>Probability: {selectedLead.probability}%</Text>
              <Text style={s.modalText}>Created: {formatDate(selectedLead.createdAt)}</Text>
            </View>
            {companyProfile && (
              <View style={s.modalSection}>
                <Text style={s.modalSectionTitle}>Company Context</Text>
                <Text style={s.modalText}>{companyProfile.name}{companyProfile.industry ? ` · ${companyProfile.industry}` : ''}</Text>
              </View>
            )}
            <View style={s.modalActions}>
              <Text style={s.modalSectionTitle}>Move to Stage</Text>
              <View style={s.stageButtons}>
                {['contacted', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'].map(st => (
                  <TouchableOpacity key={st} style={[s.stageBtn, { borderColor: getStatusColor(st) }]} onPress={() => { updateLeadStatus(selectedLead, st); setShowLeadModal(false); }}>
                    <Text style={[s.stageBtnText, { color: getStatusColor(st) }]}>{st.replace('closed_', '').replace('_', ' ')}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <TouchableOpacity style={s.deleteBtn} onPress={() => { setShowLeadModal(false); deleteLead(selectedLead); }}>
              <MaterialCommunityIcons name="delete" size={18} color="#EF4444" /><Text style={s.deleteBtnText}>Delete Lead</Text>
            </TouchableOpacity>
          </ScrollView>
        )}
      </View>
    </Modal>
  );

  const renderAddLeadModal = () => (
    <Modal visible={showAddLeadModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAddLeadModal(false)}>
      <View style={s.modalContainer}>
        <View style={s.modalHeader}>
          <Text style={s.modalTitle}>Add New Lead</Text>
          <TouchableOpacity style={s.modalClose} onPress={() => setShowAddLeadModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280" /></TouchableOpacity>
        </View>
        <ScrollView style={s.modalBody}>
          {[
            { key: 'firstName', label: 'First Name *', placeholder: 'John' },
            { key: 'lastName', label: 'Last Name', placeholder: 'Smith' },
            { key: 'email', label: 'Email *', placeholder: 'john@company.com', keyboard: 'email-address' },
            { key: 'phone', label: 'Phone', placeholder: '+44 7700 900000', keyboard: 'phone-pad' },
            { key: 'company', label: 'Company *', placeholder: 'TechCorp Ltd' },
            { key: 'jobTitle', label: 'Job Title', placeholder: 'CTO' },
            { key: 'value', label: 'Deal Value (£)', placeholder: '50000', keyboard: 'numeric' },
            { key: 'notes', label: 'Notes', placeholder: 'Add notes...', multiline: true },
          ].map(field => (
            <View key={field.key} style={s.formField}>
              <Text style={s.formLabel}>{field.label}</Text>
              <TextInput style={[s.formInput, field.multiline && { height: 80, textAlignVertical: 'top' }]}
                placeholder={field.placeholder} placeholderTextColor="#9CA3AF" value={newLead[field.key]}
                onChangeText={(v) => setNewLead({ ...newLead, [field.key]: v })}
                keyboardType={field.keyboard || 'default'} multiline={field.multiline} />
            </View>
          ))}

          <View style={s.formField}>
            <Text style={s.formLabel}>Source</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {['website', 'referral', 'linkedin', 'trade-show', 'cold-call', 'email-campaign', 'social-media'].map(src => (
                <TouchableOpacity key={src} style={[s.filterChip, newLead.source === src && { backgroundColor: PRIMARY }]} onPress={() => setNewLead({ ...newLead, source: src })}>
                  <Text style={[s.filterChipText, newLead.source === src && { color: '#FFF' }]}>{src}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          <View style={s.formField}>
            <Text style={s.formLabel}>Priority</Text>
            <View style={{ flexDirection: 'row' }}>
              {['low', 'medium', 'high', 'critical'].map(p => (
                <TouchableOpacity key={p} style={[s.filterChip, newLead.priority === p && { backgroundColor: getPriorityColor(p) }]} onPress={() => setNewLead({ ...newLead, priority: p })}>
                  <Text style={[s.filterChipText, newLead.priority === p && { color: '#FFF' }]}>{p}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <TouchableOpacity style={s.submitBtn} onPress={createLead}>
            <MaterialCommunityIcons name="check" size={20} color="#FFF" />
            <Text style={s.submitBtnText}>Create Lead</Text>
          </TouchableOpacity>
          <View style={{ height: 40 }} />
        </ScrollView>
      </View>
    </Modal>
  );

  if (isLoading) {
    return <SafeAreaView style={s.loading}><StatusBar barStyle="dark-content" /><ActivityIndicator size="large" color={PRIMARY} /><Text style={s.loadingText}>Loading leads...</Text></SafeAreaView>;
  }

  return (
    <SafeAreaView style={s.container}>
      <StatusBar barStyle="light-content" />
      {renderHeader()}
      {renderStats()}
      {renderTabs()}
      {renderContent()}
      {renderLeadDetailModal()}
      {renderAddLeadModal()}
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F3F4F6' },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F3F4F6' },
  loadingText: { fontSize: 16, color: '#6B7280', marginTop: 16 },
  header: { paddingTop: 8, paddingBottom: 16, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  headerBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  headerCenter: { flex: 1, marginLeft: 12 },
  headerTitle: { fontSize: 20, fontWeight: 'bold', color: '#FFF' },
  headerSub: { fontSize: 13, color: '#93C5FD', marginTop: 2 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 10, gap: 6 },
  chip: { flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4 },
  chipText: { color: '#93C5FD', fontSize: 11, marginLeft: 4 },

  statsRow: { backgroundColor: '#FFF', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  statCard: { alignItems: 'center', marginRight: 16, minWidth: 70 },
  statValue: { fontSize: 16, fontWeight: 'bold', marginTop: 4 },
  statLabel: { fontSize: 11, color: '#6B7280', marginTop: 2 },

  tabsRow: { backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10, marginRight: 4, borderRadius: 20 },
  activeTab: { backgroundColor: '#EFF6FF' },
  tabText: { fontSize: 13, color: '#6B7280', marginLeft: 6, fontWeight: '500' },
  activeTabText: { color: PRIMARY, fontWeight: '600' },

  filtersContainer: { backgroundColor: '#FFF', padding: 12, margin: 16, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  searchRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8 },
  searchInput: { flex: 1, fontSize: 14, color: '#1F2937', marginLeft: 8 },
  filterChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#F3F4F6', marginRight: 6, marginTop: 4 },
  filterChipText: { fontSize: 12, color: '#6B7280', fontWeight: '500' },

  section: { backgroundColor: '#FFF', margin: 16, padding: 16, borderRadius: 12, borderWidth: 1, borderColor: '#E5E7EB' },
  sectionTitle: { fontSize: 17, fontWeight: 'bold', color: '#1F2937', marginBottom: 12 },

  leadCard: { backgroundColor: '#FFF', marginHorizontal: 16, marginBottom: 8, padding: 14, borderRadius: 10, borderWidth: 1, borderColor: '#E5E7EB' },
  leadHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  leadInfo: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  avatar: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center', marginRight: 10 },
  avatarText: { color: '#FFF', fontWeight: 'bold', fontSize: 14 },
  leadName: { fontSize: 15, fontWeight: '600', color: '#1F2937' },
  leadCompany: { fontSize: 12, color: '#6B7280', marginTop: 1 },
  leadValue: { fontSize: 14, fontWeight: '700', color: '#10B981' },
  badge: { borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2, marginTop: 4 },
  badgeText: { fontSize: 11, fontWeight: '600', textTransform: 'capitalize' },
  leadFooter: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  leadMeta: { fontSize: 12, color: '#9CA3AF' },
  leadDate: { fontSize: 12, color: '#9CA3AF' },
  probBar: { height: 4, backgroundColor: '#E5E7EB', borderRadius: 2, marginTop: 8, overflow: 'hidden' },
  probFill: { height: '100%', borderRadius: 2 },

  empty: { alignItems: 'center', padding: 32 },
  emptyText: { fontSize: 16, color: '#6B7280', marginTop: 12 },
  emptyBtn: { backgroundColor: PRIMARY, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8, marginTop: 12 },
  emptyBtnText: { color: '#FFF', fontWeight: '600' },

  pipelineStage: { marginBottom: 16 },
  pipelineHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  pipelineName: { fontSize: 15, fontWeight: '600', color: '#1F2937', marginLeft: 8 },
  pipelineValue: { fontSize: 14, fontWeight: '700', color: '#10B981' },
  dot: { width: 10, height: 10, borderRadius: 5 },
  miniCard: { backgroundColor: '#F9FAFB', borderRadius: 8, padding: 10, marginBottom: 4, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  miniName: { fontSize: 13, fontWeight: '600', color: '#1F2937' },
  miniCompany: { fontSize: 12, color: '#6B7280' },
  miniValue: { fontSize: 13, fontWeight: '600', color: '#10B981' },
  moreText: { fontSize: 12, color: '#9CA3AF', marginTop: 4, textAlign: 'center' },

  contactCard: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  contactName: { fontSize: 15, fontWeight: '600', color: '#1F2937' },
  contactMeta: { fontSize: 12, color: '#6B7280', marginTop: 1 },
  scoreBadge: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  scoreText: { color: '#FFF', fontWeight: 'bold', fontSize: 12 },

  activityCard: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' },
  actIcon: { width: 32, height: 32, borderRadius: 16, justifyContent: 'center', alignItems: 'center', marginRight: 10 },
  actTitle: { fontSize: 14, fontWeight: '600', color: '#1F2937' },
  actDesc: { fontSize: 12, color: '#6B7280', marginTop: 1 },
  actMeta: { fontSize: 11, color: '#9CA3AF', marginTop: 2 },

  analyticsGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  analyticsCard: { width: (width - 64) / 2, backgroundColor: '#F9FAFB', borderRadius: 10, padding: 14, alignItems: 'center', marginBottom: 8 },
  analyticsValue: { fontSize: 18, fontWeight: 'bold', marginTop: 6 },
  analyticsLabel: { fontSize: 12, color: '#6B7280', marginTop: 4 },
  breakdownRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  breakdownLabel: { fontSize: 13, color: '#1F2937', width: 100 },
  breakdownBar: { flex: 1, height: 6, backgroundColor: '#E5E7EB', borderRadius: 3, marginHorizontal: 8, overflow: 'hidden' },
  breakdownFill: { height: '100%', backgroundColor: PRIMARY, borderRadius: 3 },
  breakdownCount: { fontSize: 13, fontWeight: '600', color: '#1F2937', width: 30, textAlign: 'right' },

  modalContainer: { flex: 1, backgroundColor: '#FFF' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  modalTitle: { fontSize: 18, fontWeight: 'bold', color: '#1F2937', flex: 1 },
  modalClose: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#F3F4F6', justifyContent: 'center', alignItems: 'center' },
  modalBody: { flex: 1, padding: 16 },
  modalSection: { marginBottom: 20 },
  modalSectionTitle: { fontSize: 16, fontWeight: '600', color: '#1F2937', marginBottom: 8 },
  modalText: { fontSize: 14, color: '#6B7280', lineHeight: 22 },
  modalActions: { marginTop: 12 },
  stageButtons: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  stageBtn: { borderWidth: 1.5, borderRadius: 8, paddingHorizontal: 14, paddingVertical: 8 },
  stageBtnText: { fontSize: 13, fontWeight: '600', textTransform: 'capitalize' },
  deleteBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 24, padding: 12, borderRadius: 8, backgroundColor: '#FEF2F2' },
  deleteBtnText: { color: '#EF4444', fontWeight: '600', marginLeft: 6 },

  formField: { marginBottom: 16 },
  formLabel: { fontSize: 14, fontWeight: '600', color: '#1F2937', marginBottom: 6 },
  formInput: { backgroundColor: '#F3F4F6', borderRadius: 8, padding: 12, fontSize: 14, color: '#1F2937', borderWidth: 1, borderColor: '#E5E7EB' },
  submitBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: PRIMARY, borderRadius: 10, padding: 14, marginTop: 8 },
  submitBtnText: { color: '#FFF', fontWeight: '700', fontSize: 16, marginLeft: 8 },
});